import { FXPServiceCatalog, FxpServiceFactory } from '@fxp/fxpservices-di';
import { UserProfileService } from '@fxp/fxpservices';

/**
 * Profile photo lookup for the `ResourceInfoCard`.
 *
 * Legacy `resource-info-card` (CE-GCM-RStaff-StaffingUX
 * `common/components/resource-info-card/resource-info-card.component.ts:152-156`) rendered the
 * avatar with `<mgt-person>` from the Microsoft Graph Toolkit, authenticating through a
 * `SimpleProvider` that acquired a `https://graph.microsoft.com/` token.
 *
 * We deliberately do NOT take that dependency. The Graph Toolkit is deprecated — retirement began
 * 2025-09-01 and completes 2026-08-28 (https://aka.ms/mgt-retirement) — and StaffingUX already
 * absorbed a Component Governance alert from it (their `SECURITY-FIX-MVS-2026-vmmw-f85q.md` forced
 * `@microsoft/mgt` -> `@microsoft/mgt-element` to shed a transitive `msal-browser`).
 *
 * Instead we call the FXP shell's own `UserProfileService.getUserThumbnail`, which is the same
 * Graph thumbnail proxy the shell uses for its header avatar (`IAppControllerScope
 * .userThumbnailPhoto`). That keeps `graph.microsoft.com` out of our `serviceEndPoints`, adds no
 * package, and inherits the shell's token handling.
 */
export interface IProfilePhotoService {
  /** Resolve an `<img src>` for an alias, or '' when unavailable. Never throws. */
  getPhotoUrl: (alias: string) => Promise<string>;
  /** Release an object URL previously returned by `getPhotoUrl`. Safe to call with ''. */
  releasePhotoUrl: (url: string) => void;
}

/** FXP resolves thumbnails by UPN, not alias (`resource-info-card.component.ts:151`). */
export const toUpn = (alias: string): string => {
  const trimmed = (alias || '').trim().toLowerCase();
  if (!trimmed) {
    return '';
  }
  return trimmed.indexOf('@') > -1 ? trimmed : `${trimmed}@microsoft.com`;
};

/**
 * `getUserThumbnail` resolves an `$http` response whose `data` is the RAW IMAGE BODY, not a URL.
 * The FXP shell itself does exactly this with the result (core-app `ngbase.bundle.js`):
 *
 *   userProfileService.getUserThumbnail(upn, false).then(function (data) {
 *     var blob = new Blob([data.data], { type: 'JPEG' });
 *     $scope.userThumbnailPhoto = (window.URL || window.webkitURL).createObjectURL(blob);
 *   });
 *
 * So we must wrap `response.data` in a Blob and mint an object URL. We still tolerate a plain
 * string (data URI / http(s) / blob URL) because some FXP builds and our own tests hand one back.
 * Anything unrecognised is treated as "no photo" so the caller falls back to initials.
 *
 * Returns `{ url, isObjectUrl }` so the caller knows whether it owns a URL that must be revoked.
 */
export const toPhotoUrl = (
  value: unknown
): { url: string; isObjectUrl: boolean } => {
  const none = { url: '', isObjectUrl: false };
  if (!value) {
    return none;
  }

  if (typeof value === 'string') {
    const raw = value.trim();
    if (!raw || raw === 'null' || raw === 'undefined') {
      return none;
    }
    if (
      raw.startsWith('data:') ||
      raw.startsWith('http://') ||
      raw.startsWith('https://') ||
      raw.startsWith('blob:')
    ) {
      return { url: raw, isObjectUrl: false };
    }
    return none;
  }

  if (typeof value !== 'object') {
    return none;
  }

  // An $http response: unwrap once, then handle the binary body.
  const envelope = value as { data?: unknown };
  const body = 'data' in envelope ? envelope.data : value;
  if (!body) {
    return none;
  }
  if (typeof body === 'string') {
    return toPhotoUrl(body);
  }

  const isBinary =
    (typeof Blob !== 'undefined' && body instanceof Blob) ||
    (typeof ArrayBuffer !== 'undefined' &&
      (body instanceof ArrayBuffer || ArrayBuffer.isView(body as ArrayBufferView)));
  if (!isBinary) {
    return none;
  }

  try {
    const blob =
      typeof Blob !== 'undefined' && body instanceof Blob
        ? body
        : new Blob([body as BlobPart], { type: 'image/jpeg' });
    if (blob.size === 0) {
      return none;
    }
    return { url: URL.createObjectURL(blob), isObjectUrl: true };
  } catch {
    return none;
  }
};

const ProfilePhotoService = (function () {
  let _instance: IProfilePhotoService;

  function createInstance(): IProfilePhotoService {
    // Resolved lazily per call: the shell may not be ready when the module is first imported.
    const getPhotoUrl = async (alias: string): Promise<string> => {
      const upn = toUpn(alias);
      if (!upn) {
        return '';
      }
      try {
        const profileService = FxpServiceFactory.getServiceInstance(
          FXPServiceCatalog.UserProfileService
        ) as UserProfileService;
        if (!profileService?.getUserThumbnail) {
          return '';
        }
        // Second arg is the caller's optional parent deferred; the shell passes `false`.
        return toPhotoUrl(await profileService.getUserThumbnail(upn, false)).url;
      } catch {
        // No shell (tests/local preview), no photo on record, or Graph refused — fall back to
        // initials rather than surfacing an error for a decorative element.
        return '';
      }
    };

    const releasePhotoUrl = (url: string): void => {
      if (url && url.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(url);
        } catch {
          /* nothing useful to do if the URL was already revoked */
        }
      }
    };

    return { getPhotoUrl, releasePhotoUrl };
  }

  return {
    getSingleton: (): IProfilePhotoService => {
      if (!_instance) {
        _instance = createInstance();
      }
      return _instance;
    },
  };
})();

export default ProfilePhotoService;
